import { useMemo } from 'react';
import {
  GetCallQualityMetricsResponse,
  Blip,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-quality/v1/api.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Heading, SkeletonLoader, Text } from '@frontend/design-system';
import { HydratedCallRecord } from '../types';
import {
  CallQualityFeedback,
  IssuesDetectedSection,
  MetricSection,
  RecommendationsSection,
  TimelineSection,
} from './call-quality-components';
import { createMetricData } from './call-quality-utils';
import { MetricData, TimelineBarDetail } from './types';

type CallQualityScreenProps = {
  call?: HydratedCallRecord;
  data?: GetCallQualityMetricsResponse;
  isLoading: boolean;
  onTimelineClick?: (data: { label: string; details?: TimelineBarDetail[]; blip: Blip }) => void;
};

const CallLabel = ({ call }: { call: HydratedCallRecord }) => {
  const { t } = useTranslation('calls');

  const contact = call.callerName || call.callerNumber;
  const icon =
    call.direction === 'outbound' ? (
      <Icon color='light' name='phone-outgoing-small' />
    ) : (
      <Icon color='light' name='phone-incoming-small' />
    );
  const label =
    call.direction === 'outbound'
      ? t('Call to {{name}}', { name: contact })
      : t('Call from {{name}}', { name: contact });

  return (
    <div
      style={{
        gridArea: 'label',
        display: 'grid',
        alignItems: 'center',
        gap: theme.spacing(1),
        gridTemplateColumns: 'repeat(4, minmax(0px,max-content))',
      }}
    >
      {icon}
      <Text as='span' size='medium'>
        {label}
      </Text>
      <Text as='span' size='medium'>
        •
      </Text>
      <Text as='span' size='medium'>
        {dayjs(call.answeredAt).format('ddd MMM D, YYYY, h:mm A')}
      </Text>
    </div>
  );
};

const CallQualityScreenHeader = ({ call, onBack }: { call: HydratedCallRecord; onBack: () => void }) => {
  const { t } = useTranslation('calls');

  return (
    <div
      style={{
        display: 'grid',
        alignItems: 'center',
        columnGap: theme.spacing(1),
        gridTemplateColumns: 'auto 1fr',
        gridTemplateAreas: '"back title" ". label"',
      }}
    >
      <Button iconName='caret-left' variant='secondary' css={{ gridArea: 'back' }} onClick={onBack} />

      <Heading level={2} css={{ margin: 0, gridArea: 'title' }}>
        {t('Network Performance')}
      </Heading>
      {call && <CallLabel call={call} />}
    </div>
  );
};

const CallQualityScreenContent = ({ call, data, isLoading, onTimelineClick }: CallQualityScreenProps) => {
  const { t } = useTranslation('calls');

  const metricData: MetricData[] = useMemo(
    () => [
      {
        type: 'mosMin',
        values: [
          createMetricData({
            type: 'mos',
            value: data?.minMosScore || 0,
          }),
        ],
      },
      {
        type: 'packetLossMax',
        values: [
          createMetricData({
            type: 'packetLoss',
            value: data?.maxPacketLossPercentage.inbound || 0,
            direction: 'inbound',
          }),
          createMetricData({
            type: 'packetLoss',
            value: data?.maxPacketLossPercentage.outbound || 0,
            direction: 'outbound',
          }),
        ],
      },
      {
        type: 'jitterMax',
        values: [
          createMetricData({
            type: 'jitter',
            value: data?.maxJitterInMillisecond.inbound || 0,
            direction: 'inbound',
          }),
          createMetricData({
            type: 'jitter',
            value: data?.maxJitterInMillisecond.outbound || 0,
            direction: 'outbound',
          }),
        ],
      },
    ],
    [data]
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
      }}
    >
      {data && !isLoading && (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: 'repeat(auto-fit, auto)',
            padding: theme.spacing(3),
            gap: theme.spacing(2),
          }}
        >
          <IssuesDetectedSection variant='screen' />
          <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
          <RecommendationsSection />
          <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
          <MetricSection data={metricData} subtitle={t('These values highlight the worst call quality detected.')} />
          <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
          <TimelineSection
            blips={data.blips ?? []}
            startTime={data?.callAnsweredTime}
            endTime={data?.callEndTime}
            onClick={(_data) => onTimelineClick?.(_data)}
          />
          <CallQualityFeedback
            startTime={data?.callAnsweredTime}
            channelId={call?.channelId ?? ''}
            locationId={call?.locationId ?? ''}
          />
        </div>
      )}
      {isLoading && (
        <div
          style={{
            height: '100%',
            padding: theme.spacing(3),
            display: 'grid',
            gridTemplateRows: 'repeat(3, max-content)',
            gap: theme.spacing(2),
          }}
        >
          <SkeletonLoader height={150} width={450} />
          <SkeletonLoader height={300} width={450} />
          <SkeletonLoader height={150} width={450} />
        </div>
      )}
      {!data && !isLoading && (
        <Text size='medium' css={{ textAlign: 'center', marginTop: theme.spacing(4) }}>
          {t('No call quality data available for this call.')}
        </Text>
      )}
    </div>
  );
};

export const CallQualityScreen = {
  Header: CallQualityScreenHeader,
  Content: CallQualityScreenContent,
};
